import { useEffect, useState } from 'react';

/** The version baked into THIS bundle at build time. */
const currentVersion = __APP_VERSION__;

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
}

const DEFAULT_INTERVAL_MS = 15 * 60 * 1000;

/** MODULE-scoped so the throttle survives remounts and StrictMode double-effects. */
let lastCheckedAt = 0;

/**
 * Is a newer FRONTEND deployed than the one this tab is running?
 *
 * This used to ask the BACKEND — it fetched /api/health and compared the
 * server's version to this bundle's. Those are two separate deployments, and
 * the backend cannot possibly know whether a newer frontend exists. So the
 * moment the backend shipped one patch ahead (0.8.1, against a frontend built
 * at 0.8.0), every user got "A new version is available!" — permanently.
 * Clicking Refresh reloaded the same bundle, the comparison ran again, and the
 * banner came straight back. There was no newer version to go and get.
 *
 * Ask the right question instead: fetch the frontend's OWN deployed manifest
 * (public/version.json — written from package.json at prebuild and shipped
 * beside index.html), uncached, and compare it to what we are running. That is
 * the only thing that can truthfully answer "should this tab reload?".
 *
 * Polling rules (unchanged, and the reason this is the app's only poller):
 * one check on mount, then at most one per interval, only while the tab is
 * visible, plus an opportunistic check on refocus — which is the moment the
 * answer actually matters.
 */
export function useVersionCheck(interval: number = DEFAULT_INTERVAL_MS) {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckedAt < interval) return;
      lastCheckedAt = Date.now();

      try {
        // Same origin — this is the frontend asking about the frontend.
        // `no-store` because a cached manifest answers with the version we
        // already have, which is the one answer it must never give.
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;

        const data = await res.json();
        const deployed: string | undefined = data?.version;
        if (cancelled || !deployed || !currentVersion) return;

        setLatestVersion(deployed);
        setNewVersionAvailable(compareVersions(deployed, currentVersion) > 0);
      } catch {
        // Best-effort. A failed check must never nag.
      }
    };

    check();
    const id = setInterval(check, interval);
    document.addEventListener('visibilitychange', check);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, [interval]);

  return { newVersionAvailable, currentVersion, latestVersion };
}
